import { InsightsHeadcountFragment } from 'generated/resources/graphql'

import { parseBackendDate } from 'modules/core/components/DateNavigator/dateFormatUtils'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { dateToString } from 'modules/core/lib/dates'

import DateRangeNavigator from 'modules/insights/components/DateRangeNavigator'

type Props = {
  currentDate: Date
  headcountEvolution: InsightsHeadcountFragment[]
  onRangeChange: (date: Date) => void
}

export const HeadcountDateRangeNavigator = ({
  currentDate,
  headcountEvolution,
  onRangeChange,
}: Props) => {
  const firstDate = headcountEvolution[0]?.startDate
  const lastDate = headcountEvolution[headcountEvolution.length - 1]?.endDate

  const minDate = firstDate ? parseBackendDate(firstDate) : undefined
  const maxDate = lastDate ? parseBackendDate(lastDate) : undefined

  const currentHeadcountIndex = useUnsafeMemo(() => {
    const currentDateString = dateToString(currentDate, 'backend')

    return headcountEvolution.findIndex((entry) => entry.endDate === currentDateString)
  }, [currentDate, headcountEvolution])

  const currentHeadcount = headcountEvolution[currentHeadcountIndex]

  const handlePreviousRange = useUnsafeCallback(() => {
    const previousDate = headcountEvolution[currentHeadcountIndex - 1]?.endDate

    if (!previousDate) {
      return
    }

    onRangeChange(parseBackendDate(previousDate))
  }, [currentHeadcountIndex, headcountEvolution, onRangeChange])

  const handleNextRange = useUnsafeCallback(() => {
    const nextDate = headcountEvolution[currentHeadcountIndex + 1]?.endDate

    if (!nextDate) {
      return
    }

    onRangeChange(parseBackendDate(nextDate))
  }, [currentHeadcountIndex, headcountEvolution, onRangeChange])

  if (!currentHeadcount) {
    return null
  }

  return (
    <DateRangeNavigator
      range={[
        parseBackendDate(currentHeadcount.startDate),
        parseBackendDate(currentHeadcount.endDate),
      ]}
      minDate={minDate}
      maxDate={maxDate}
      onPrevRange={handlePreviousRange}
      onNextRange={handleNextRange}
      animate
    />
  )
}
